import {
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactNode,
  type Ref,
} from 'react';

import { cn } from '@/shared/lib/cn';
import { countText, FONTES_DO_EDITOR, sanitizeHtml } from '@/shared/lib/textoRico';

import { FieldLabel } from './FieldLabel';

/** Tamanhos da barra. O OnPoint abre em 12px. */
const TAMANHOS = ['10px', '11px', '12px', '14px', '16px', '18px', '24px'] as const;

const FORMATOS = [
  { valor: 'p', rotulo: 'Paragrafo' },
  { valor: 'h1', rotulo: 'Titulo 1' },
  { valor: 'h2', rotulo: 'Titulo 2' },
  { valor: 'h3', rotulo: 'Titulo 3' },
] as const;

interface Comando {
  comando: string;
  rotulo: string;
  simbolo: ReactNode;
  /** Comando com estado (negrito ligado ou nao), anunciado por aria-pressed. */
  alterna?: boolean;
}

const grupos: Comando[][] = [
  [
    { comando: 'bold', rotulo: 'Negrito', simbolo: <b>B</b>, alterna: true },
    { comando: 'italic', rotulo: 'Italico', simbolo: <i>I</i>, alterna: true },
    { comando: 'underline', rotulo: 'Sublinhado', simbolo: <u>U</u>, alterna: true },
    { comando: 'strikeThrough', rotulo: 'Tachado', simbolo: <s>S</s>, alterna: true },
    {
      comando: 'superscript',
      rotulo: 'Sobrescrito',
      simbolo: (
        <span>
          X<sup>2</sup>
        </span>
      ),
      alterna: true,
    },
    {
      comando: 'subscript',
      rotulo: 'Subscrito',
      simbolo: (
        <span>
          X<sub>2</sub>
        </span>
      ),
      alterna: true,
    },
  ],
  [
    { comando: 'insertUnorderedList', rotulo: 'Lista com marcadores', simbolo: '•' },
    { comando: 'insertOrderedList', rotulo: 'Lista numerada', simbolo: '1.' },
  ],
  [
    { comando: 'justifyLeft', rotulo: 'Alinhar a esquerda', simbolo: '⇤' },
    { comando: 'justifyCenter', rotulo: 'Centralizar', simbolo: '↔' },
    { comando: 'justifyRight', rotulo: 'Alinhar a direita', simbolo: '⇥' },
    { comando: 'justifyFull', rotulo: 'Justificar', simbolo: '≡' },
  ],
  [
    { comando: 'removeFormat', rotulo: 'Limpar formatacao', simbolo: 'Tx' },
    { comando: 'undo', rotulo: 'Desfazer', simbolo: '↶' },
    { comando: 'redo', rotulo: 'Refazer', simbolo: '↷' },
  ],
];

/**
 * Executa um comando de edicao do navegador. O comando e antigo, e e o unico que
 * todos os navegadores implementam sobre a selecao do usuario sem biblioteca; o
 * resultado passa pelo saneamento antes de chegar a quem usa o editor.
 */
function executar(comando: string, valor?: string): boolean {
  if (typeof document.execCommand !== 'function') return false;
  try {
    return document.execCommand(comando, false, valor);
  } catch {
    return false;
  }
}

function estado(comando: string): boolean {
  if (typeof document.queryCommandState !== 'function') return false;
  try {
    return document.queryCommandState(comando);
  } catch {
    return false;
  }
}

/**
 * O que quem usa o editor pode pedir a ele, por `editorRef`: inserir texto no cursor,
 * como o "Incluir TAG" dos textos da modalidade (P-PAR-2).
 */
export interface RichTextEditorHandle {
  /**
   * Insere o texto onde o cursor estava (ou no fim, se o cursor nunca esteve no
   * editor) e entrega o HTML saneado a quem usa.
   */
  inserirTexto: (texto: string) => void;
}

export interface RichTextEditorProps {
  label: string;
  /** HTML gravado. E saneado antes de aparecer. */
  value: string;
  /** Recebe sempre o HTML saneado, ou texto vazio quando nao ha conteudo. */
  onChange: (html: string) => void;
  /** Limite de caracteres do texto (sem as marcas). Passar dele vira erro do campo. */
  maxLength?: number | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  help?: string | false | undefined;
  /** Altura minima da area de texto, em linhas aproximadas. */
  rows?: number | undefined;
  /** Acesso a insercao de texto no cursor (Incluir TAG). */
  editorRef?: Ref<RichTextEditorHandle> | undefined;
}

const formatoDoNumero = new Intl.NumberFormat('pt-BR');

/** Blocos em que o texto inserido sem cursor entra, do fim para dentro. */
const blocosDoFim = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'li',
  'blockquote',
  'ul',
  'ol',
]);

/**
 * Editor de texto rico (P-FUN-2b), como o "Editor otimizado para Word" do OnPoint:
 * barra de formatacao (negrito, italico, sublinhado, tachado, sobrescrito, subscrito,
 * fonte Arial ou Calibri, tamanho, paragrafo e titulo, listas e alinhamento) e o
 * contador de caracteres e palavras no rodape.
 *
 * O valor e HTML saneado (`shared/lib/textoRico.ts`): o que a pessoa digita, cola do
 * Word ou de outra pagina passa pela lista de permissao antes de sair do editor, e o
 * HTML gravado e saneado de novo antes de aparecer. Colar traz a formatacao permitida
 * e descarta o resto, inclusive o estilo do Word.
 */
export function RichTextEditor({
  label,
  value,
  onChange,
  maxLength,
  required,
  disabled,
  placeholder,
  hint,
  error,
  help,
  rows = 8,
  editorRef,
}: RichTextEditorProps) {
  const id = useId();
  const rotuloId = `${id}-label`;
  const contadorId = `${id}-contador`;
  const dicaId = `${id}-dica`;
  const erroId = `${id}-erro`;
  const area = useRef<HTMLDivElement>(null);
  // O ultimo HTML entregue a quem usa: quando o valor de fora e o mesmo, o conteudo
  // da area nao e reescrito, e o cursor nao pula para o comeco a cada letra.
  const emitido = useRef<string | null>(null);
  const [ativos, setAtivos] = useState<Record<string, boolean>>({});
  // O ultimo cursor dentro da area: o botao de fora (Incluir TAG) tira o foco, e o
  // texto precisa entrar onde a pessoa estava.
  const cursor = useRef<Range | null>(null);

  useEffect(() => {
    const elemento = area.current;
    if (!elemento) return;
    const limpo = sanitizeHtml(value);
    if (emitido.current !== null && limpo === emitido.current) return;
    elemento.innerHTML = limpo;
    emitido.current = limpo;
  }, [value]);

  function emitir() {
    const elemento = area.current;
    if (!elemento) return;
    const limpo = sanitizeHtml(elemento.innerHTML);
    if (limpo === emitido.current) return;
    emitido.current = limpo;
    onChange(limpo);
  }

  function guardarCursor() {
    const selecao =
      typeof window.getSelection === 'function' ? window.getSelection() : null;
    if (!selecao || selecao.rangeCount === 0 || !area.current) return;
    const faixa = selecao.getRangeAt(0);
    if (area.current.contains(faixa.commonAncestorContainer)) {
      cursor.current = faixa.cloneRange();
    }
  }

  useImperativeHandle(
    editorRef,
    () => ({
      inserirTexto(texto: string) {
        const elemento = area.current;
        if (!elemento || disabled || texto === '') return;
        elemento.focus();
        const selecao =
          typeof window.getSelection === 'function' ? window.getSelection() : null;
        const faixa =
          cursor.current && elemento.contains(cursor.current.commonAncestorContainer)
            ? cursor.current
            : null;
        if (selecao && faixa) {
          selecao.removeAllRanges();
          selecao.addRange(faixa);
        }
        if (!executar('insertText', texto)) {
          // Sem o comando do navegador, o texto entra no cursor guardado, ou no fim.
          const no = document.createTextNode(texto);
          if (faixa) {
            faixa.deleteContents();
            faixa.insertNode(no);
            faixa.setStartAfter(no);
            faixa.collapse(true);
            cursor.current = faixa;
          } else {
            // Sem cursor, o texto entra no fim do ultimo paragrafo, e nao solto
            // depois dele.
            let alvo: Element = elemento;
            while (
              alvo.lastElementChild &&
              blocosDoFim.has(alvo.lastElementChild.tagName.toLowerCase())
            ) {
              alvo = alvo.lastElementChild;
            }
            alvo.appendChild(no);
          }
        }
        emitir();
        guardarCursor();
      },
    }),
    // emitir le o estado pelo ref; so o desligado muda o comportamento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled],
  );

  function atualizarEstados() {
    const proximo: Record<string, boolean> = {};
    for (const grupo of grupos) {
      for (const item of grupo) {
        if (item.alterna) proximo[item.comando] = estado(item.comando);
      }
    }
    setAtivos(proximo);
  }

  function aplicar(comando: string, valor?: string) {
    if (disabled) return;
    area.current?.focus();
    // Estilo em CSS, e nao a marca <font>: a fonte e o tamanho saem como span com
    // estilo, que o saneamento conhece.
    executar('styleWithCSS', 'true');
    executar(comando, valor);
    emitir();
    atualizarEstados();
  }

  function aplicarTamanho(tamanho: string) {
    if (disabled) return;
    area.current?.focus();
    executar('styleWithCSS', 'true');
    // O comando do navegador so conhece os tamanhos de 1 a 7. Marca-se com o 7 e
    // troca-se pelo tamanho escolhido, no lugar exato da selecao.
    executar('fontSize', '7');
    const elemento = area.current;
    if (elemento) {
      elemento.querySelectorAll('font[size="7"]').forEach((fonte) => {
        const span = document.createElement('span');
        span.style.fontSize = tamanho;
        while (fonte.firstChild) span.appendChild(fonte.firstChild);
        fonte.replaceWith(span);
      });
      elemento.querySelectorAll<HTMLElement>('span').forEach((span) => {
        if (
          span.style.fontSize === 'xxx-large' ||
          span.style.fontSize === '-webkit-xxx-large'
        ) {
          span.style.fontSize = tamanho;
        }
      });
    }
    emitir();
  }

  function colar(evento: ClipboardEvent<HTMLDivElement>) {
    evento.preventDefault();
    if (disabled) return;
    const dados = evento.clipboardData;
    const html = dados.getData('text/html');
    const texto = dados.getData('text/plain');
    const limpo = html
      ? sanitizeHtml(html)
      : texto
          .split(/\r?\n/)
          .map((linha) =>
            linha.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
          )
          .join('<br>');
    if (!executar('insertHTML', limpo)) {
      // Sem o comando (navegador sem suporte), o texto entra no fim, ja saneado.
      area.current?.insertAdjacentHTML('beforeend', limpo);
    }
    emitir();
  }

  // A contagem segue o valor que volta de quem usa o editor (campo controlado).
  const contagem = useMemo(() => countText(value), [value]);
  const passou = maxLength !== undefined && contagem.characters > maxLength;
  const erroDoCampo =
    error ??
    (passou
      ? `O texto passou do limite de ${formatoDoNumero.format(maxLength)} caracteres.`
      : undefined);
  const descricao = [
    contadorId,
    hint && !erroDoCampo ? dicaId : null,
    erroDoCampo ? erroId : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel
        id={rotuloId}
        htmlFor={id}
        label={label}
        className="text-xs font-semibold text-ink-700"
        help={help}
      />

      <div
        className={cn(
          'overflow-hidden rounded-md border bg-surface',
          erroDoCampo ? 'border-danger-500' : 'border-line-strong',
          disabled && 'bg-surface-sunken',
        )}
      >
        <div
          role="toolbar"
          aria-label={`Formatacao de ${label}`}
          aria-controls={id}
          className="flex flex-wrap items-center gap-1 border-b border-line bg-surface-sunken px-2 py-1.5"
          // A barra nao rouba o foco: a selecao continua no texto quando o botao e
          // apertado, e o comando vale para ela.
          onMouseDown={(evento) => {
            if ((evento.target as HTMLElement).tagName !== 'SELECT')
              evento.preventDefault();
          }}
        >
          <select
            aria-label="Paragrafo"
            disabled={disabled}
            defaultValue="p"
            onChange={(evento) => aplicar('formatBlock', `<${evento.target.value}>`)}
            className="h-7 rounded border border-line bg-surface px-1 text-2xs"
          >
            {FORMATOS.map((formato) => (
              <option key={formato.valor} value={formato.valor}>
                {formato.rotulo}
              </option>
            ))}
          </select>
          <select
            aria-label="Fonte"
            disabled={disabled}
            defaultValue=""
            onChange={(evento) => {
              if (evento.target.value) aplicar('fontName', evento.target.value);
            }}
            className="h-7 rounded border border-line bg-surface px-1 text-2xs"
          >
            <option value="">Fonte</option>
            {FONTES_DO_EDITOR.map((fonte) => (
              <option key={fonte} value={fonte}>
                {fonte}
              </option>
            ))}
          </select>
          <select
            aria-label="Tamanho"
            disabled={disabled}
            defaultValue=""
            onChange={(evento) => {
              if (evento.target.value) aplicarTamanho(evento.target.value);
            }}
            className="h-7 rounded border border-line bg-surface px-1 text-2xs"
          >
            <option value="">Tamanho</option>
            {TAMANHOS.map((tamanho) => (
              <option key={tamanho} value={tamanho}>
                {tamanho}
              </option>
            ))}
          </select>

          {grupos.map((grupo, indice) => (
            <div
              key={indice}
              className="flex items-center gap-0.5 border-l border-line pl-1"
            >
              {grupo.map((item) => (
                <button
                  key={item.comando}
                  type="button"
                  title={item.rotulo}
                  aria-label={item.rotulo}
                  aria-pressed={
                    item.alterna ? Boolean(ativos[item.comando]) : undefined
                  }
                  disabled={disabled}
                  onClick={() => aplicar(item.comando)}
                  className={cn(
                    'inline-flex size-7 items-center justify-center rounded text-xs text-ink-700',
                    'hover:bg-ink-100 focus-visible:outline-2 focus-visible:outline-brand-500',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    item.alterna &&
                      ativos[item.comando] &&
                      'bg-brand-50 text-brand-700',
                  )}
                >
                  {item.simbolo}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div
          id={id}
          ref={area}
          role="textbox"
          aria-multiline="true"
          aria-labelledby={rotuloId}
          aria-describedby={descricao || undefined}
          aria-invalid={erroDoCampo ? true : undefined}
          aria-required={required || undefined}
          aria-disabled={disabled || undefined}
          aria-placeholder={placeholder}
          contentEditable={!disabled}
          suppressContentEditableWarning
          tabIndex={disabled ? -1 : 0}
          onInput={emitir}
          onBlur={() => {
            guardarCursor();
            // Na saida, a area mostra exatamente o que foi entregue, ja saneado: o
            // que o comando do navegador acrescentou de estranho some da vista.
            emitir();
            if (area.current && emitido.current !== null) {
              area.current.innerHTML = emitido.current;
            }
          }}
          onPaste={colar}
          onKeyUp={() => {
            guardarCursor();
            atualizarEstados();
          }}
          onMouseUp={() => {
            guardarCursor();
            atualizarEstados();
          }}
          style={{ minHeight: `${rows * 1.5}rem` }}
          className={cn(
            'prose-editor max-h-[32rem] overflow-y-auto px-3 py-2 text-sm text-ink-900',
            'focus:outline-none',
            '[&_h1]:text-lg [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold',
            '[&_h3]:text-sm [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-6',
            '[&_ul]:list-disc [&_ul]:pl-6 [&_p]:my-1',
            disabled && 'cursor-not-allowed text-ink-500',
          )}
        />

        <div
          id={contadorId}
          aria-live="polite"
          className={cn(
            'flex justify-end gap-3 border-t border-line px-3 py-1 text-2xs tabular-nums',
            passou ? 'text-danger-700' : 'text-ink-500',
          )}
        >
          <span>
            {formatoDoNumero.format(contagem.characters)}
            {maxLength !== undefined
              ? ` de ${formatoDoNumero.format(maxLength)}`
              : ''}{' '}
            {contagem.characters === 1 ? 'caractere' : 'caracteres'}
          </span>
          <span>
            {formatoDoNumero.format(contagem.words)}{' '}
            {contagem.words === 1 ? 'palavra' : 'palavras'}
          </span>
        </div>
      </div>

      {hint && !erroDoCampo ? (
        <p id={dicaId} className="text-2xs text-ink-500">
          {hint}
        </p>
      ) : null}
      {erroDoCampo ? (
        <p id={erroId} className="text-2xs font-medium text-danger-700">
          {erroDoCampo}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Texto rico so de leitura. O HTML gravado e saneado de novo antes de aparecer: o
 * texto que veio de outra fonte (migracao, API) nao passa pelo editor.
 */
export function RichTextView({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  const limpo = sanitizeHtml(html);
  if (!limpo) return <p className="text-sm text-ink-400">Sem texto.</p>;
  return (
    <div
      className={cn(
        'text-sm text-ink-900 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_p]:my-1',
        className,
      )}
      // O conteudo e o HTML saneado pela lista de permissao de textoRico.ts.
      dangerouslySetInnerHTML={{ __html: limpo }}
    />
  );
}
